<script setup lang="ts">
/**
 * PageLayout — the website-chrome page shell for the scrolling "page" surfaces
 * (sign-in, browse, library, pattern detail, profile), as opposed to the
 * immersive grid shell in `ToolLayout.vue`.
 *
 * Why it exists: every page repeats the same skeleton — a centred, gutter-padded
 * column with a header whose title and actions need to reflow on a phone. The
 * established markup put the title and its action button on one line
 * (`justify-between`), which crowds long titles against a button on a narrow
 * screen. Here the title takes its own line *above* the actions on mobile and
 * only moves inline (`sm:` and up) when there's room (#133). Centralising it
 * means #130–#133 can drop their hand-rolled headers onto one primitive.
 *
 * Contract: this is a *scrolling* page (the opposite of ToolLayout). It fills at
 * least the viewport so short pages still push any sticky footer down, but it
 * does not trap scroll — the page scrolls normally. `containerClass` controls the
 * column width so a narrow form (sign-in) and a wide grid (library) can share the
 * same shell; it defaults to `max-w-7xl` (the gutter the existing pages use) and,
 * because `cn` is tailwind-merge, a caller's `max-w-*` overrides it cleanly.
 *
 * Slots: `title` (heading), `actions` (buttons/menus shown beside or below the
 * title), and the default slot for page content. The header only renders when a
 * `title` or `actions` slot is supplied.
 */
import { computed, useSlots, type HTMLAttributes } from 'vue'
import { cn } from '@/lib/utils'

const props = defineProps<{
  class?: HTMLAttributes['class']
  /** Tailwind max-width (+ centring) for the content column. */
  containerClass?: HTMLAttributes['class']
}>()

const slots = useSlots()
const hasHeader = computed(() => !!slots.title || !!slots.actions)

// One shared container so the header and content align to the same gutter.
// The header's safe-area-inset-top padding keeps it clear of a notch when the
// page is opened full-bleed (e.g. installed to the home screen); normal browser
// chrome makes the inset zero, so it's a no-op there.
const container = computed(() =>
  cn('mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8', props.containerClass)
)
</script>

<template>
  <div
    :class="cn('flex min-h-dvh flex-col bg-background text-foreground', props.class)"
    data-testid="page-layout"
  >
    <header v-if="hasHeader" class="pt-[env(safe-area-inset-top)]" data-testid="page-header">
      <div
        :class="
          cn(
            container,
            'flex flex-col gap-3 py-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-8'
          )
        "
      >
        <div class="min-w-0" data-testid="page-title">
          <slot name="title" />
        </div>
        <div
          v-if="!!slots.actions"
          class="flex flex-wrap items-center gap-2"
          data-testid="page-actions"
        >
          <slot name="actions" />
        </div>
      </div>
    </header>

    <!-- pb-8 keeps page content off the bottom edge (a sticky footer or the
         viewport); when there's no header the main also owns the top gutter. -->
    <main :class="cn('flex-1 pb-8', !hasHeader && 'pt-8')">
      <div :class="container">
        <slot />
      </div>
    </main>
  </div>
</template>
